import { DateTime, type WeekdayNumbers as WeekdayNumbersType } from 'luxon';
import { useMemo } from 'react';
import type { Weekday } from 'rrule';
import { WeekDayNumbers } from '@/pages/Schedule/constants/recurrence';
import { Button, Flex, type StackProps } from '@/ui';

type Props = Omit<StackProps, 'onChange'> & {
  value: Weekday[];
  onChange: (updatedWeekday: Weekday[]) => void;
};

const getOrderedWeekdays = (): Weekday[] => {
  // Days in the `WeekNumbers` array are stored in the same order as RRule for
  // easy indexing. This places Monday first, but we want Sunday to appear
  // first visually, so we change it via CSS instead.
  const days = [...WeekDayNumbers];
  const lastDay = days.pop();
  return lastDay ? [lastDay, ...days] : days;
};

export const WeekdaySelect = ({ value, onChange, ...props }: Props) => {
  const handleWeekdayChange = (weekday: Weekday) => {
    const updatedWeekday = value.includes(weekday)
      ? value.filter((day) => day !== weekday)
      : [...value, weekday];
    onChange(updatedWeekday);
  };

  // Convert Weekday[] => number[]
  const selectedWeekdays = useMemo(
    () => value.map(({ weekday }) => weekday),
    [value]
  );

  return (
    <Flex justify="space-between" w="225px" {...props}>
      {getOrderedWeekdays().map((weekday) => (
        <Day
          canRemove={value.length > 1 || value[0] !== weekday}
          checked={selectedWeekdays.includes(weekday.weekday)}
          key={weekday.weekday}
          weekday={weekday}
          onClick={() => handleWeekdayChange(weekday)}
        />
      ))}
    </Flex>
  );
};

type DayProps = {
  weekday: Weekday;
  checked: boolean;
  canRemove: boolean;
  onClick: () => void;
};

const Day = ({ weekday, checked, canRemove, onClick }: DayProps) => {
  const formattedWeek = DateTime.now()
    .set({ weekday: (weekday.weekday + 1) as WeekdayNumbersType })
    .toFormat('EEE')
    .charAt(0);

  return (
    <Flex
      align="center"
      aria-checked={checked}
      as={Button}
      bg={checked ? 'brand.500' : 'white'}
      borderColor={checked ? 'brand.500' : 'customgray.light'}
      borderRadius="50%"
      borderWidth="1px"
      color={checked ? 'white' : undefined}
      cursor={canRemove ? 'pointer' : 'not-allowed'}
      fontSize="12"
      fontWeight="400"
      h="26px"
      justify="center"
      minW="auto"
      role="checkbox"
      variant="unstyled"
      w="26px"
      _focusVisible={{
        outline: '1px solid',
        outlineColor: 'brand.500',
      }}
      onClick={canRemove ? onClick : undefined}
    >
      {formattedWeek}
    </Flex>
  );
};
